import { useState } from 'react';
import { Plus, List, ClipboardCheck } from 'lucide-react';
import { CreateInspectionForm } from './inspection/CreateInspectionForm';
import { InspectionList } from './inspection/InspectionList';
import { InspectionChecklist } from './inspection/InspectionChecklist';
import { supabase } from '../lib/supabase';

type View = 'list' | 'create' | 'checklist' | 'report';

export function InspectionManager() {
  const [currentView, setCurrentView] = useState<View>('list');
  const [selectedInspectionId, setSelectedInspectionId] = useState<string | null>(null);
  const [selectedPropertyName, setSelectedPropertyName] = useState<string>('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleInspectionCreated = async (inspectionId: string) => {
    setSelectedInspectionId(inspectionId);

    const { data } = await supabase
      .from('inspections')
      .select('properties(name)')
      .eq('id', inspectionId)
      .single();

    if (data?.properties) {
      setSelectedPropertyName(data.properties.name);
    }

    setCurrentView('checklist');
  };

  const handleSelectInspection = async (inspectionId: string) => {
    setSelectedInspectionId(inspectionId);

    const { data } = await supabase
      .from('inspections')
      .select('properties(name)')
      .eq('id', inspectionId)
      .single();

    if (data?.properties) {
      setSelectedPropertyName(data.properties.name);
    }

    setCurrentView('checklist');
  };

  const handleChecklistComplete = () => {
    setRefreshTrigger((prev) => prev + 1);
    setCurrentView('list');
    setSelectedInspectionId(null);
  };

  const handleViewReport = (inspectionId: string) => {
    setSelectedInspectionId(inspectionId);
    setCurrentView('report');
  };

  return (
    <div>
      {currentView !== 'checklist' && currentView !== 'report' && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <ClipboardCheck className="text-blue-600" size={32} />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Property Inspections</h1>
                <p className="text-sm text-gray-600">Schedule and manage property inspection checklists</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentView('list')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentView === 'list'
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <List size={18} />
                All Inspections
              </button>
              <button
                onClick={() => setCurrentView('create')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentView === 'create'
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <Plus size={18} />
                New Inspection
              </button>
            </div>
          </div>
        </div>
      )}

      {currentView === 'list' && (
        <InspectionList
          onSelectInspection={handleSelectInspection}
          onViewReport={handleViewReport}
          refreshTrigger={refreshTrigger}
        />
      )}

      {currentView === 'create' && (
        <CreateInspectionForm
          onSuccess={handleInspectionCreated}
          onCancel={() => setCurrentView('list')}
        />
      )}

      {currentView === 'checklist' && selectedInspectionId && (
        <InspectionChecklist
          inspectionId={selectedInspectionId}
          propertyName={selectedPropertyName}
          onComplete={handleChecklistComplete}
        />
      )}

      {currentView === 'report' && selectedInspectionId && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Inspection Report</h2>
            <button
              onClick={() => setCurrentView('list')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              Back to List
            </button>
          </div>
          <div className="text-center py-12 text-gray-500">
            <p>Inspection report view coming soon</p>
            <p className="text-sm mt-1">This will display a detailed report of the completed inspection</p>
          </div>
        </div>
      )}
    </div>
  );
}
